import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../firebaseConfig';
import { useOrganization } from '../contexts/OrganizationContext';
import { useSessionTimeout } from './order-management/hooks/useSessionTimeout';
import { useTranslation } from '../hooks/useTranslation';
import PlatformLayout from './PlatformLayout';
import PlatformText from './PlatformText';
import ErrorBoundary from './ErrorBoundary';
import { getPlatformClass, isNative, normalizePartyNameForDisplay, getPlatformType } from '../utils/platformUtils';
import { canAccessDashboard } from '../utils/trialUtils';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip as ChartJSTooltip,
  Legend,
} from 'chart.js';
import LoadingScreen from './LoadingScreen';
import './Dashboard.css';
import { useDashboardAnalytics } from './dashboard/useDashboardAnalytics';
import ProgressRingCard from './dashboard/ProgressRingCard';
import AnalyticsList from './dashboard/AnalyticsList';
import DashboardChart from './dashboard/DashboardChart';
import PartiesNotOrderedCard from './dashboard/PartiesNotOrderedCard';
import DashboardLocked from './DashboardLocked';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  ChartJSTooltip,
  Legend
);

function Dashboard() {
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const { organizationId, trialStatus, organizationData } = useOrganization();
  const { t } = useTranslation('dashboard');
  const { analytics, userMap, loading, error } = useDashboardAnalytics(organizationId);
  const [salesPeriodOrders, setSalesPeriodOrders] = useState('month');
  const [salesPeriodRevenue, setSalesPeriodRevenue] = useState('month');
  const platformClass = getPlatformClass();
  const isMobilePlatform = isNative() || platformClass.includes('pwa');
  const platformType = getPlatformType();
  const isMobileAppBar = ['ios', 'android', 'ios-pwa', 'android-pwa'].includes(platformType);

  useSessionTimeout(user, navigate);

  const isTrialSubscription = trialStatus?.subscriptionStatus === 'trial';
  const subscriptionPlan =
    (isTrialSubscription ? 'trial' : (trialStatus?.subscriptionPlan || organizationData?.subscriptionPlan)) ||
    'basic';
  const bypassLimits = organizationData?.bypassLimits || false;

  if (!canAccessDashboard(subscriptionPlan, bypassLimits)) {
    return <DashboardLocked />;
  }

  // Month dropdown options
  const monthNames = [
    t('months.january'), t('months.february'), t('months.march'), t('months.april'), 
    t('months.may'), t('months.june'), t('months.july'), t('months.august'), 
    t('months.september'), t('months.october'), t('months.november'), t('months.december')
  ];
  const currentMonthIdx = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  const monthOptions = [
    { value: 'month', label: t('periods.currentMonth') },
    ...monthNames.map((name, idx) => ({ value: `month-${idx}`, label: name })),
    { value: 'all', label: t('periods.allTime') }
  ];

  // Memoized salespeople stats for orders
  const getSalespeopleStats = () => {
    if (salesPeriodOrders === 'all') {
      const totalOrders = analytics.topSalespeople.reduce((sum, [, count]) => sum + count, 0);
      return analytics.topSalespeople.map(([salesperson, count], idx) => ({
        key: salesperson,
        name: userMap[salesperson] || salesperson,
        rank: idx + 1,
        count,
        percent: totalOrders > 0 ? (count / totalOrders) * 100 : 0
      }));
    } else {
      let monthIdx;
      if (salesPeriodOrders === 'month') {
        monthIdx = currentMonthIdx;
      } else if (salesPeriodOrders.startsWith('month-')) {
        monthIdx = parseInt(salesPeriodOrders.split('-')[1], 10);
      }
      const year = currentYear;
      const salesMap = {};
      let total = 0;
      if (analytics.ordersRaw) {
        analytics.ordersRaw.forEach(order => {
          const d = order.createdAt.toDate();
          if (d.getMonth() === monthIdx && d.getFullYear() === year) {
            if (order.createdBy) {
              salesMap[order.createdBy] = (salesMap[order.createdBy] || 0) + 1;
              total++;
            }
            if (order.assignedTo && order.assignedTo !== order.createdBy) {
              salesMap[order.assignedTo] = (salesMap[order.assignedTo] || 0) + 1;
              total++;
            }
          }
        });
      }
      const arr = Object.entries(salesMap).sort(([, a], [, b]) => b - a).slice(0, 5);
      return arr.map(([salesperson, count], idx) => ({
        key: salesperson,
        name: userMap[salesperson] || salesperson,
        rank: idx + 1,
        count,
        percent: total > 0 ? (count / total) * 100 : 0
      }));
    }
  };

  // Memoized salespeople stats for revenue
  const getRevenueSalespeopleStats = () => {
    if (salesPeriodRevenue === 'all') {
      const totalRevenue = analytics.topRevenueSalespeople.reduce((sum, [, revenue]) => sum + revenue, 0);
      return analytics.topRevenueSalespeople.map(([salesperson, revenue], idx) => ({
        key: salesperson,
        name: userMap[salesperson] || salesperson,
        rank: idx + 1,
        revenue,
        percent: totalRevenue > 0 ? (revenue / totalRevenue) * 100 : 0
      }));
    } else {
      let monthIdx;
      if (salesPeriodRevenue === 'month') {
        monthIdx = currentMonthIdx;
      } else if (salesPeriodRevenue.startsWith('month-')) {
        monthIdx = parseInt(salesPeriodRevenue.split('-')[1], 10);
      }
      const year = currentYear;
      const salesMap = {};
      let total = 0;
      if (analytics.ordersRaw) {
        analytics.ordersRaw.forEach(order => {
          const d = order.createdAt.toDate();
          if (d.getMonth() === monthIdx && d.getFullYear() === year) {
            const orderValue = Number(order.orderValue || 0);
            if (order.createdBy) {
              salesMap[order.createdBy] = (salesMap[order.createdBy] || 0) + orderValue;
              total += orderValue;
            }
            if (order.assignedTo && order.assignedTo !== order.createdBy) {
              salesMap[order.assignedTo] = (salesMap[order.assignedTo] || 0) + orderValue;
              total += orderValue;
            }
          }
        });
      }
      const arr = Object.entries(salesMap).sort(([, a], [, b]) => b - a).slice(0, 5);
      return arr.map(([salesperson, revenue], idx) => ({
        key: salesperson,
        name: userMap[salesperson] || salesperson,
        rank: idx + 1,
        revenue,
        percent: total > 0 ? (revenue / total) * 100 : 0
      }));
    }
  };

  // Prepare customers not ordered recently data
  const partiesNotOrderedItems = analytics.partyLastOrderDates.map(([party, date], idx) => {
    const daysAgo = Math.floor((new Date() - date) / (1000 * 60 * 60 * 24));
    return {
      key: party,
      name: normalizePartyNameForDisplay(party),
      daysAgo,
      date: date.toLocaleDateString(),
      rank: idx + 1
    };
  });

  // Prepare top customers and revenue customers data
  const topPartiesItems = analytics.topParties.map(([party, count], idx) => ({
    key: party,
    name: normalizePartyNameForDisplay(party),
    rank: idx + 1,
    count
  }));
  const topRevenuePartiesItems = analytics.topRevenueParties.map(([party, revenue], idx) => ({
    key: party,
    name: normalizePartyNameForDisplay(party),
    rank: idx + 1,
    revenue
  }));

  // Chart data
  const monthlyOrdersData = {
    labels: analytics.months,
    datasets: [
      {
        label: t('legends.orders'),
        data: analytics.monthlyOrders,
        backgroundColor: isMobilePlatform ? 'rgba(53, 162, 235, 0.6)' : 'rgba(53, 162, 235, 0.5)',
        borderColor: 'rgba(53, 162, 235, 1)',
        borderWidth: 1,
      },
    ],
  };
  const monthlyRevenueData = {
    labels: analytics.months,
    datasets: [
      {
        label: t('legends.revenue'),
        data: analytics.monthlyRevenue,
        borderColor: isMobilePlatform ? 'rgba(75, 192, 192, 1)' : 'rgba(75, 192, 192, 1)',
        backgroundColor: isMobilePlatform ? 'rgba(75, 192, 192, 0.3)' : 'rgba(75, 192, 192, 0.2)',
        tension: 0.1,
        fill: true,
      },
    ],
  };
  const mobileChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(255, 255, 255, 0.2)',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: ${context.parsed.y}`;
          }
        }
      }
    },
    scales: {
      x: {
        display: true,
        grid: { display: false },
        ticks: {
          color: 'var(--text-secondary)',
          font: { size: isMobilePlatform ? 10 : 12 }
        }
      },
      y: {
        display: true,
        grid: { color: 'rgba(0, 0, 0, 0.05)' },
        ticks: {
          color: 'var(--text-secondary)',
          font: { size: isMobilePlatform ? 10 : 12 }
        }
      }
    },
    elements: {
      point: { radius: isMobilePlatform ? 3 : 4, hoverRadius: isMobilePlatform ? 5 : 6 },
      line: { borderWidth: isMobilePlatform ? 2 : 3 },
      bar: { borderWidth: isMobilePlatform ? 1 : 2 }
    }
  };

  if (loading) {
    return <LoadingScreen message="Loading analytics..." variant="fullscreen" />;
  }
  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <ErrorBoundary componentName="Dashboard">
      <PlatformLayout>
        <div className={`dashboard-container ${platformClass} ${isMobileAppBar ? '' : 'mobile-optimized'}`}>
        <div className="profile-header-wrapper">
          {isMobileAppBar ? (
            <div className="mobile-app-bar">
              <button onClick={() => navigate('/orders')} className="back-icon-button" aria-label={t('actions.goBack')}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
              </button>
              <PlatformText variant="h1" className="profile-title">{t('title')}</PlatformText>
            </div>
          ) : (
            <div className="saas-header">
              <button 
                className="saas-back-button"
                onClick={() => navigate('/orders')}
                aria-label="Go back"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 12H5M12 19l-7-7 7-7"/>
                </svg>
              </button>
              <div className="saas-header-content">
                <PlatformText variant="h1" className="saas-title">{t('title')}</PlatformText>
                <PlatformText variant="body" className="saas-subtitle">{t('subtitle')}</PlatformText>
              </div>
            </div>
          )}
        </div>
        <div className="dashboard-grid">
          {/* Progress Ring Cards */}
          <div className="progress-rings-container">
            <ProgressRingCard
              title={t('stats.totalOrders')}
              value={analytics.currentMonthOrders}
              previousValue={analytics.previousMonthOrders}
              color="blue"
              size={isMobilePlatform ? "medium" : "large"}
              summary={`${Math.abs(analytics.currentMonthOrders - analytics.previousMonthOrders) === 0 ? t('summaries.noChangeFromLastMonth') : analytics.currentMonthOrders > analytics.previousMonthOrders ? `${Math.abs(growth(analytics.currentMonthOrders, analytics.previousMonthOrders)).toFixed(1)}% ${t('summaries.moreOrdersThanLastMonth')}` : `${Math.abs(growth(analytics.currentMonthOrders, analytics.previousMonthOrders)).toFixed(1)}% ${t('summaries.fewerOrdersThanLastMonth')}`}`}
            />
            <ProgressRingCard
              title={t('stats.totalRevenue')}
              value={analytics.currentMonthRevenue}
              previousValue={analytics.previousMonthRevenue}
              unit="₹"
              color="green"
              size={isMobilePlatform ? "medium" : "large"}
              summary={`${Math.abs(analytics.currentMonthRevenue - analytics.previousMonthRevenue) === 0 ? t('summaries.noChangeFromLastMonth') : analytics.currentMonthRevenue > analytics.previousMonthRevenue ? `${Math.abs(growth(analytics.currentMonthRevenue, analytics.previousMonthRevenue)).toFixed(1)}% ${t('summaries.higherRevenueThanLastMonth')}` : `${Math.abs(growth(analytics.currentMonthRevenue, analytics.previousMonthRevenue)).toFixed(1)}% ${t('summaries.lessRevenueThanLastMonth')}`}`}
            />
            <ProgressRingCard
              title={t('stats.averageOrderValue')}
              value={analytics.currentMonthAOV}
              previousValue={analytics.previousMonthAOV}
              unit="₹"
              color="purple"
              size={isMobilePlatform ? "medium" : "large"}
              summary={`${Math.abs(analytics.currentMonthAOV - analytics.previousMonthAOV) === 0 ? t('summaries.noChangeFromLastMonth') : analytics.currentMonthAOV > analytics.previousMonthAOV ? `${Math.abs(growth(analytics.currentMonthAOV, analytics.previousMonthAOV)).toFixed(1)}% ${t('summaries.higherAOVThanLastMonth')}` : `${Math.abs(growth(analytics.currentMonthAOV, analytics.previousMonthAOV)).toFixed(1)}% ${t('summaries.lowerAOVThanLastMonth')}`}`}
            />
          </div>
          {/* Charts */}
          <div className="charts-container">
            <DashboardChart
              title={t('stats.monthlyOrders')}
              type="bar"
              data={monthlyOrdersData}
              options={mobileChartOptions}
            />
            <DashboardChart
              title={t('stats.monthlyRevenue')}
              type="line"
              data={monthlyRevenueData}
              options={mobileChartOptions}
            />
            <PartiesNotOrderedCard
              title={t('stats.customersNotOrdered')}
              items={partiesNotOrderedItems}
            />
            <AnalyticsList
              title={t('stats.topRevenueSalespeople')}
              items={getRevenueSalespeopleStats()}
              dropdown={
                <select
                  id="revenueSalesPeriod"
                  value={salesPeriodRevenue}
                  onChange={e => setSalesPeriodRevenue(e.target.value)}
                  style={{
                    fontSize: 15,
                    padding: '8px 12px',
                    borderRadius: 8,
                    border: '1px solid var(--border-color)',
                    width: '100%',
                    boxSizing: 'border-box',
                    background: 'var(--card-background)',
                    color: 'var(--text-primary)',
                    marginTop: 4,
                    marginBottom: 4,
                    outline: 'none',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
                  }}
                  className="salespeople-dropdown"
                >
                  {monthOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              }
              renderRight={item => (
                <>
                  <span className="order-count" style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>₹{item.revenue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  <span className="order-percent" style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>{item.percent.toFixed(1)}% of total</span>
                </>
              )}
              emptyMessage={t('messages.noData')}
            />
          </div>
          {/* Top Lists */}
          <div className="analytics-lists">
            <AnalyticsList
              title={t('tables.topCustomers')}
              items={topPartiesItems}
              renderRight={item => (
                <span className="order-count" style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>{item.count} {t('legends.orders')}</span>
              )}
              emptyMessage={t('messages.noData')}
            />
            <AnalyticsList
              title={t('stats.topRevenueCustomers')}
              items={topRevenuePartiesItems}
              renderRight={item => (
                <span className="order-count" style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>₹{item.revenue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              )}
              emptyMessage={t('messages.noData')}
            />
            <AnalyticsList
              title={t('tables.topSalespeople')}
              items={getSalespeopleStats()}
              dropdown={
                <select
                  id="salesPeriod"
                  value={salesPeriodOrders}
                  onChange={e => setSalesPeriodOrders(e.target.value)}
                  style={{
                    fontSize: 15,
                    padding: '8px 12px',
                    borderRadius: 8,
                    border: '1px solid var(--border-color)',
                    width: '100%',
                    boxSizing: 'border-box',
                    background: 'var(--card-background)',
                    color: 'var(--text-primary)',
                    marginTop: 4,
                    marginBottom: 4,
                    outline: 'none',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
                  }}
                  className="salespeople-dropdown"
                >
                  {monthOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              }
              renderRight={item => (
                <>
                  <span className="order-count" style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>{item.count} {t('legends.orders')}</span>
                  <span className="order-percent" style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>{item.percent.toFixed(1)}% of total</span>
                </>
              )}
              emptyMessage={t('messages.noData')}
            />
          </div>
        </div>
      </div>
    </PlatformLayout>
    </ErrorBoundary>
  );
}

function growth(current, previous) {
  if (!previous || previous === 0) return 0;
  return ((current - previous) / previous) * 100;
}

export default Dashboard; 